'use client';

import { c as _c } from "react/compiler-runtime";
import { useContext, useMemo, useCallback } from 'react';
import { DateTime } from 'luxon';
import { isPresent } from '@farmai-shared/LFUtils';
import { useLFSearchParams } from '@services/useLFSearchParams';
import { useQueryOrg } from '@services/useQueryOrg';
import { RootStoreContext } from '@stores/rootStore';
import { ISeason } from '@stores/SeasonStore';

// Choose the most recent season whose datetime is <= now (fallback: first)
const pickDefaultSeason = (seasons: ISeason[]): ISeason | null => {
  if (!seasons?.length) return null;
  const now = DateTime.now();
  const sorted = [...seasons].sort((a, b) => a.datetime > b.datetime ? 1 : -1);
  let target = sorted[0];
  for (const s of sorted) {
    if (s.datetime > now) break;
    target = s;
  }
  return target ?? null;
};
type SeasonLite = {
  id: string;
  name: string;
} | null;
export const useActiveSeason = () => {
  const $ = _c(18);
  const {
    uiStore
  } = useContext(RootStoreContext);
  const {
    searchParams,
    setParams
  } = useLFSearchParams();
  const seasonIdFromUrl = searchParams.get("filter[season_id]");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["useActiveSeason:seasons"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== uiStore) {
    t1 = {
      queryKey: t0,
      queryFn: () => uiStore.currentOrganization.fetchSeasons(),
      refetchOnWindowFocus: false
    };
    $[1] = uiStore;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data: t2,
    isLoading: isLoadingSeasons
  } = useQueryOrg(t1);
  let t3;
  if ($[3] !== t2) {
    t3 = t2 === undefined ? [] : t2;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const seasons = t3;
  const {
    data: seasonById,
    isLoading: isLoadingSeasonById
  } = useQueryOrg({
    queryKey: ["useActiveSeason:byId", seasonIdFromUrl],
    queryFn: () => (!seasonIdFromUrl ? Promise.resolve(null) : uiStore.currentOrganization?.fetchSeason(seasonIdFromUrl).then(_temp)) as Promise<SeasonLite>,
    enabled: isPresent(seasonIdFromUrl),
    refetchOnWindowFocus: false
  });
  const t4 = seasons as ISeason[];
  let t5;
  if ($[5] !== t4) {
    t5 = pickDefaultSeason(t4);
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const s = t5;
  let t6;
  if ($[7] !== s) {
    t6 = s ? {
      id: s.id,
      name: s.name
    } : null;
    $[7] = s;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const defaultSeason = t6;
  const activeSeason = seasonById || null;
  const isLoading = isLoadingSeasons || isLoadingSeasonById;
  let t7;
  if ($[9] !== setParams) {
    t7 = nextId => {
      setParams([{
        key: "filter[season_id]",
        value: nextId
      }]);
    };
    $[9] = setParams;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const setSeason = t7;
  let t8;
  if ($[11] !== activeSeason || $[12] !== defaultSeason || $[13] !== isLoading || $[14] !== seasonIdFromUrl || $[15] !== seasons || $[16] !== setSeason) {
    t8 = {
      seasonId: seasonIdFromUrl,
      activeSeason,
      defaultSeason,
      seasons,
      isLoading,
      setSeason
    };
    $[11] = activeSeason;
    $[12] = defaultSeason;
    $[13] = isLoading;
    $[14] = seasonIdFromUrl;
    $[15] = seasons;
    $[16] = setSeason;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
};
function _temp(res) {
  return res ? {
    id: res.id,
    name: res.name
  } : null;
}